import { Playlist, TrackCategory } from './playlist.types'

/* eslint-disable camelcase */
export type OperationStatus = 'QUEUED' | 'STARTED' | 'COMPLETED' | 'FAILED'
export type OperationPlan = 'free' | 'premium' | 'pro'

export type OperationMasterResult = {
  masteredMp3: string // deprecated
  masteredWav: string // deprecated
  files: {
    [key: string]: string
    mastered: string
    // masteredHighRes: string // deprecated
    original: string
  }
}

export type OperationDenoiserResult = {
  files: {
    [key: string]: string
    denoised: string
    // denoisedHighRes: string // deprecated
    original: string
  }
}

export type OperationBeatChordsResult = {
  bpm: number
  key: string
  estimatedTuning: number | string
  files: {
    [key: string]: string
    beats: string
    chords: string
    beatchords: string
    metronome: string
  }
}

export type OperationFingerprintResult = {
  coverArt: string
  files: {
    fingerprint: string
  }
  isrc: string
  lyrics: string
  subtitle: string
  title: string
}

export type OperationLyricsResult = {
  files: {
    lyrics: string
  }
}

export type OperationSegmentationResult = {
  files: {
    segmentation: string
    segments: string
    segments_enhanced: string
  }
}

export type OperationSplitResult = {
  zipFileUrl?: string
  playableUrls?: string[] // deprecated
  files: {
    [key: string]: string | undefined
    bass?: string
    bassHighRes?: string
    drums?: string
    drumsHighRes?: string
    other?: string
    otherHighRes?: string
    piano?: string
    pianoHighRes?: string
    vocals: string
    vocalsHighRes?: string
  }
  filesAux?: {
    vocals: string
  }
  downloadLinks?: string[]
}

export type OperationSplitParams = {
  type?: string
  stems?: string[]
  hifi?: boolean
  average: any
  priority: number
  context?: {
    plan?: 'free' | 'premium' | 'pro'
    copyOf?: string
    useMaestro?: boolean
    beatchordsMaestro?: boolean
  }
}

export type OperationMasterParams = {
  bit: number
  limiter: true
  normalize: true
  targetLufs: number
  priority: number
  type?: any
  referenceAudio: {
    name: string
    input: string
    location: string
    provider: string
  }
}

export type OperationDenoiserParams = {
  priority: number
  maxDuration: number
  type: any
}

export type OperationLyricsParams = {
  vocalsPath: string
  language: string
  priority: number
  maxDuration: number
  type: any
}

export type OperationSegmentationParams = {
  beatsPath: string
}

type OperationResult =
  | OperationMasterResult
  | OperationDenoiserResult
  | OperationSplitResult
  | OperationBeatChordsResult
  | OperationFingerprintResult
  | OperationSegmentationResult
  | OperationLyricsResult

type OperationParams =
  | OperationMasterParams
  | OperationSplitParams
  | OperationDenoiserParams
  | OperationLyricsParams
  | OperationSegmentationParams
  | OperationBeatChordsParams

/** TODO: remove --- operations */
export interface TaskOperations<
  R extends OperationResult = any,
  P extends OperationParams = any
> {
  current: boolean
  outdated: boolean
  outdatedReason: string | null
  updateInProgress: boolean
  isOwner: boolean
  id: string
  name: string
  status: OperationStatus
  result: R
  params: P
  createdAt: string
  startedAt: string
  completedAt: string
}

export enum TaskPostVideoOrientation {
  LANDSCAPE = 'LANDSCAPE',
  PORTRAIT = 'PORTRAIT'
}

export interface TaskPost {
  id: string
  content: string
  name: string
  avatar: string
  createdAt: string
  attachments?: {
    url: string
    name: string
  }[]
  video?: {
    title?: string
    thumbnail?: string
    video?: string
    duration?: number
    orientation?: TaskPostVideoOrientation
  }[]
}

export type Separate = {
  stems?: {
    stem?: string
    path?: string
    url?: string
    adding?: boolean
  }[]
  params?: {
    plan?: OperationPlan
    maxDuration?: number
  }
  id: string
  status?: OperationStatus
  legacy?: {
    workflow?: string
    type?: string
  } | null
  updateInProgress?: boolean
  updateType?: 'ADAPT' | 'CHANGE'
}

export type Beatschords = {
  beats?: {
    bpm?: number
    bpmFile?: {
      path?: string
      url?: string
    }
    beatsFile?: {
      path?: string
      url?: string
    }
  }
  key?: string
  tuning?: string
  chords?: {
    path?: string
    url?: string
    content?: JSON
  }
  params?: {
    plan?: OperationPlan
    maxDuration?: number
  }
  status?: OperationStatus
  metronome?: {
    stem?: 'metronome' | 'metronome_half' | 'metronome_double'
    tempo?: 'DEFAULT' | 'HALF' | 'DOUBLE'
    speed?: number
    path?: string
    url?: string
  }[]
  updateInProgress?: boolean
}

export type Segmentation = {
  params?: {
    plan?: OperationPlan
    maxDuration?: number
  }
  status?: OperationStatus
  segments?: {
    path?: string
    url?: string
    content?: JSON
  }
  updateInProgress?: boolean
}

export type Lyrics = {
  params?: {
    plan?: OperationPlan
    maxDuration?: number
  }
  status?: OperationStatus
  lyrics?: {
    path?: string
    url?: string
    content?: JSON
  }
  language?: string
  updateInProgress?: boolean
}

export interface Summary {
  separate: Separate
  beatschords: Beatschords
  segmentation: Segmentation
  lyrics: Lyrics
  original: {
    duration?: number
    sampleRate?: number
    bitRate?: number
    url?: string
    path?: string
    bpm?: string
    key?: string
    artist?: string
    title?: string
    album?: string
    genre?: string
    comment?: string
    format?: string
    size?: number
    audioChannelLayout?: string
    createdAt?: string
    cover?: string
  }
}
export interface Task {
  id: string
  file: {
    name: string
    location: string | null
    provider: 'FILESYSTEM' | 'URL' | 'RECORD'
    duration: number | null
  }
  isDemo: boolean
  operations: TaskOperations[]
  playlists: Playlist[]
  metadata: {
    id: string
    key: string
    value: string | null
    updateAt: string
  }[]
  isPublic: boolean
  isOwner: boolean
  owner: string
  summary: { v1: Summary }
  posts?: {
    totalCount: number
    notes: TaskPost[]
  }
  category: TrackCategory
  isShared: boolean
}

export interface SplitTask extends Task {
  refetchTask(): void
}

export type OperationBeatChordsParams = {
  context?: {
    plan: 'free' | 'premium' | 'pro'
    client?: {
      name?: string
      version?: string
    }
    useMaestro?: boolean
    processedBy?: string
    reprocessOf?: string
    beatchordsMaestro?: boolean
  }
  priority?: number
  maxDuration?: number
  experimental?: boolean
}

export type SummaryOperationBeatChordsResult = {
  bpm?: number
  bpmFile?: {
    path?: string
    url?: string
    content?: JSON
  }
  beatsFile?: {
    path?: string
    url?: string
    content?: JSON
  }
}
